import { MATERIALS } from '@sim/content/materials';
import type { MaterialId } from '@sim/content/materials';
import { RECIPES, isTupleRecipe } from '@sim/content/recipes';
import type { RecipeDef } from '@sim/content/recipes';
import { isJoinMachine } from '@sim/content/machine-defs';
import type {
  JoinMachineEntity,
  MachineEntity,
  SourceYield,
  ObservableEntity,
} from '@sim/core/entities';
import { observableAtInput, traceObservableChain } from '@sim/core/routing';
import { observableCap } from '@sim/core/observable';
import { ticksToMs } from '@sim/content/timing';
import type { SimState } from '@sim/core/state';

const LINE_WIDTH = 40;

function materialIdentifier(material: MaterialId): string {
  return MATERIALS[material].label.toLowerCase();
}

/** `a`, `b`, `c`, … — one letter per drawn/bought slot, in order. */
function slotLetter(index: number): string {
  return String.fromCharCode(97 + index);
}

function wrapArray(items: string[]): string {
  const oneLine = `[${items.join(', ')}]`;
  if (oneLine.length <= LINE_WIDTH) return oneLine;
  const inner = items.map((item) => `  ${item},`).join('\n');
  return `[\n${inner}\n]`;
}

/** `of`'s values are individual call arguments, not an array — same line-wrap threshold as `from`. */
function wrapCallArgs(fnName: string, items: string[]): string {
  const oneLine = `${fnName}(${items.join(', ')})`;
  if (oneLine.length <= LINE_WIDTH) return oneLine;
  const inner = items.map((item) => `  ${item},`).join('\n');
  return `${fnName}(\n${inner}\n)`;
}

/** A recipe that consumes a tuple destructures it in the callback's parameter list, which is the
 * whole difference between the two arities on the page. */
function mapCallback(recipe: RecipeDef): string {
  const params = recipe.from.map(materialIdentifier);
  const binding = isTupleRecipe(recipe) ? `([${params.join(', ')}])` : params[0];
  return `map(${binding} => new ${MATERIALS[recipe.to].label}(${params.join(', ')}))`;
}

function operatorFor(state: SimState, machine: Exclude<MachineEntity, JoinMachineEntity>): string {
  if (machine.kind === 'map') {
    if (!machine.config) return 'map(/* choose a recipe */)';
    return mapCallback(RECIPES[machine.config.recipeId]);
  }
  if (machine.kind === 'filter') {
    if (!machine.config) return 'filter(/* choose materials to allow */)';
    const allowLabels = machine.config.allow.map((material) => MATERIALS[material].label);
    if (allowLabels.length === 1) return `filter(value => value instanceof ${allowLabels[0]})`;
    return `filter(value => [${allowLabels.join(', ')}].some(T => value instanceof T))`;
  }
  if (machine.kind === 'takeUntil') {
    const subject = machine.config && state.subjects[machine.config.subjectId];
    return subject ? `takeUntil(${subject.name})` : 'takeUntil(/* choose a Subject */)';
  }
  if (machine.kind === 'switchMap') {
    if (!machine.config) return 'switchMap(/* choose a sell method */)';
    return `switchMap(value => marketService.${machine.config.method}(value))`;
  }
  if (!machine.config) return 'take(/* choose a count */)';
  return `take(${machine.config.count})`;
}

function pipeableOperators(state: SimState, machines: readonly MachineEntity[]): string[] {
  return machines
    .filter(
      (machine): machine is Exclude<MachineEntity, JoinMachineEntity> => !isJoinMachine(machine),
    )
    .map((machine) => operatorFor(state, machine));
}

function pipeExpr(emission: string, operators: string[]): string {
  if (operators.length === 0) return emission;
  return `${emission}.pipe(\n${operators.map((operator) => `    ${operator}`).join(',\n')}\n)`;
}

/** `zip(a$, b$)` and `combineLatest([a$, b$])` — each spelled the way its own RxJS signature reads,
 * never the pipeable `zipWith`/`combineLatestWith` form: the in-game tool is literally named after
 * the standalone function, and generated code should show the exact operator the player placed. */
function joinCall(join: JoinMachineEntity, primary: string, partner: string): string {
  return join.kind === 'zip'
    ? `zip(${primary}, ${partner})`
    : `combineLatest([${primary}, ${partner}])`;
}

/**
 * The stream on the join's other input is named, not inlined — it is a real observable declared
 * above, and the join subscribes to it internally, which is why nothing ever calls `.subscribe()`
 * on it directly.
 *
 * Neither join takes a callback, and neither ever appears with one: they emit the pair exactly as
 * they packed it, and turning that pair into a product is the job of whatever `map` the player puts
 * behind it.
 */
function joinedExpression(
  state: SimState,
  emission: string,
  leading: string[],
  machines: readonly MachineEntity[],
  joinIndex: number,
): string {
  const join = machines[joinIndex] as JoinMachineEntity;
  const preJoin = pipeExpr(emission, [
    ...leading,
    ...pipeableOperators(state, machines.slice(0, joinIndex)),
  ]);
  const partner = observableAtInput(state, join, 1);
  const partnerVar = partner ? observableVarName(state, partner) : '/* connect a second stream */';
  return pipeExpr(
    joinCall(join, preJoin, partnerVar),
    pipeableOperators(state, machines.slice(joinIndex + 1)),
  );
}

/**
 * The observer, built from whatever the player actually installed in the Subscriber's columns. The
 * object form appears only once `error` holds something: `.subscribe(fn)` and `.subscribe({ next: fn })`
 * are the same subscription, and the shorthand is the one the player will meet everywhere until
 * there is a second callback to pass.
 *
 * Which sink the proceeds land in is deliberately absent — `deposit` routes cash and research
 * itself, so naming the destination here would invent a distinction the installed method doesn't make.
 */
function observerArg(state: SimState): string {
  const box = state.subscriber;
  const next = box?.functions.next[0];
  const error = box?.functions.error[0];
  const nextArg = next ? `value => TreasuryService.${next}(value)` : null;
  if (!error) return nextArg ?? '';
  const nextEntry = nextArg ? `next: ${nextArg}, ` : '';
  return `{ ${nextEntry}error: err => AuditService.${error}(err) }`;
}

/** No kind settles on its materials before it emits them, so naming always reads `yields`. */
function observableMaterials(observable: ObservableEntity): MaterialId[] {
  return observable.yields.map((y) => y.material);
}

function observableVarBase(materials: MaterialId[]): string {
  const distinct = new Set(materials);
  return distinct.size === 1 ? MATERIALS[materials[0]].label.toLowerCase() : 'ore';
}

function observableSuffix(state: SimState, observable: ObservableEntity): string {
  const observables = Object.values(state.observables);
  const index = observables.findIndex((s) => s.id === observable.id);
  return observables.length > 1 ? `${index + 1}` : '';
}

/** Stable `X$` variable name for an observable, numbered only when more than one observable exists. */
export function observableVarName(state: SimState, observable: ObservableEntity): string {
  return `${observableVarBase(observableMaterials(observable))}${observableSuffix(state, observable)}$`;
}

/** `sub`, numbered only when more than one observable exists — paired 1:1 with {@link observableVarName}. */
export function subVarName(state: SimState, observable: ObservableEntity): string {
  return `sub${observableSuffix(state, observable)}`;
}

function oddsSummary(yields: SourceYield[]): string {
  const total = yields.reduce((sum, y) => sum + y.weight, 0);
  return yields
    .map((y) => `${Math.round((y.weight / total) * 100)}% ${MATERIALS[y.material].label}`)
    .join(', ');
}

/**
 * Every material an observable has produced was resolved by `sourceService` — whichever source, market,
 * or other origin sits under the observable tile, picking each value by odds the player never sees
 * (see `editing.ts`'s placement check for what an observable can sit on). So generated code never
 * invents a material value inline for `of` or `from`; it always looks the observable up first, reads
 * its resolved materials into a local, then reads each slot off *that* — the player bought or
 * drew the slot, they didn't build the lookup themselves, but they can still see it was fetched
 * from somewhere real. `sourceService` and the object it returns are never declared anywhere in
 * the generated code, same idiom as `TreasuryService` in {@link observerArg}.
 *
 * The lookup key is the observable's grid position, not its (material-derived, display-only)
 * variable name — two different physical observables that happen to produce the same material must
 * never resolve to the same key, and a stream's display name/numbering can shift independently
 * of where its observable actually sits.
 */
function observableKey(observable: ObservableEntity): string {
  return `${observable.position.x},${observable.position.y}`;
}

function materialsLookup(observable: ObservableEntity, materialsVar: string): string {
  return `const ${materialsVar} = sourceService.get('${observableKey(observable)}').getMaterials();`;
}

function slotValues(materialsVar: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => `${materialsVar}.get('${slotLetter(i)}')`);
}

/** `of` names one value per slot the player bought, whether or not it has been drawn yet; `from` can
 * only name the ones it has actually drawn so far. */
function slotCount(observable: ObservableEntity): number {
  return observable.kind === 'of' ? observable.slotCosts.length : observable.sequence.length;
}

/**
 * The always-current shape of an observable's stream — its emitted items and any operators between it
 * and wherever the chain currently ends — with no `.subscribe()`. Pure function of topology, so
 * it's safe to regenerate on every layout edit without touching the separate activity log.
 */
export function declareObservable(
  state: SimState,
  observable: ObservableEntity,
  varName: string,
): string {
  const { machines } = traceObservableChain(state, observable);
  const materialsVar = `${varName.replace('$', '')}Materials`;
  const values = slotValues(materialsVar, slotCount(observable));
  const emission = emissionFor(observable, values);
  const leading = leadingOperators(observable, materialsVar);

  const joinIndex = machines.findIndex(isJoinMachine);
  const expression =
    joinIndex === -1
      ? pipeExpr(emission, [...leading, ...pipeableOperators(state, machines)])
      : joinedExpression(state, emission, leading, machines, joinIndex);

  const declaration = `const ${varName} = ${expression};`;
  const statement = needsMaterialsLookup(observable)
    ? `${materialsLookup(observable, materialsVar)}\n${declaration}`
    : declaration;

  const showsOdds = observable.yields.length > 1 && observable.cursor < observableCap(observable);
  return showsOdds
    ? `${statement} // + weighted draws: ${oddsSummary(observable.yields)}`
    : statement;
}

function emissionFor(observable: ObservableEntity, values: string[]): string {
  if (observable.kind === 'of') return wrapCallArgs('of', values);
  if (observable.kind === 'from') return `from(${wrapArray(values)})`;
  const period = observable.periodTicks;
  return `interval(${period === null ? '/* choose a period */' : ticksToMs(period)})`;
}

/**
 * `interval` emits an ascending index rather than a value, so the source draw it stands for has to
 * be spelled out as a `map` of its own before any operator the player actually placed — every other
 * creation kind gets its materials straight out of the emission itself.
 */
function leadingOperators(observable: ObservableEntity, materialsVar: string): string[] {
  if (observable.kind !== 'interval' || observable.periodTicks === null) return [];
  return [`map(i => ${materialsVar}.get(i))`];
}

function needsMaterialsLookup(observable: ObservableEntity): boolean {
  return observable.kind === 'interval'
    ? observable.periodTicks !== null
    : slotCount(observable) > 0;
}

export function subscribeStatement(
  state: SimState,
  observable: ObservableEntity,
  varName: string,
  subName: string,
  first: boolean,
): string {
  const { sink } = traceObservableChain(state, observable);
  const arg = sink ? observerArg(state) : '';
  const keyword = first ? 'let ' : '';
  return `${keyword}${subName} = ${varName}.subscribe(${arg});`;
}

export function unsubscribeStatement(subName: string): string {
  return `${subName}.unsubscribe();`;
}

/**
 * True for a stream that ends by being folded into a join rather than by reaching a subscriber.
 * `zip()` subscribes it internally, so it is declared but never subscribed to in its own right —
 * and it has to be declared *before* the stream that names it.
 */
export function feedsAnotherStream(state: SimState, observable: ObservableEntity): boolean {
  return traceObservableChain(state, observable).joinedAt !== null;
}

export function declareAllObservables(state: SimState): string {
  const observables = Object.values(state.observables);
  if (observables.length === 0) return '// build an `of` observable onto a source to get started';
  const joinedFirst = [
    ...observables.filter((observable) => feedsAnotherStream(state, observable)),
    ...observables.filter((observable) => !feedsAnotherStream(state, observable)),
  ];
  const declarations = joinedFirst.map((observable) =>
    declareObservable(state, observable, observableVarName(state, observable)),
  );
  return [...declareSubjects(state), ...declarations].join('\n\n');
}

/**
 * Channels come first because everything else names them, and `Subject<void>` because what they
 * carry is the fact that something happened, never a value — a signal, not a packet.
 */
function declareSubjects(state: SimState): string[] {
  return Object.values(state.subjects).map(
    (subject) => `const ${subject.name} = new Subject<void>();`,
  );
}
